import React from 'react';
import {
  Box,
  Typography,
  Container,
  Grid,
  Card,
  CardContent,
  Button,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import { motion } from 'framer-motion';
import { LocationOn, Phone, Public } from '@mui/icons-material';

const Offices = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const offices = [
    {
      id: 1,
      city: 'Trivandrum',
      country: 'India',
      address: 'TC 79/342(5), 2nd Floor, CPS Plaza, Opp Kia Motors, NH Bypass Venpalavatom, Anayara (PO), Trivandrum 695029.',
      phone: '+91 7356475308, +91 7736715551',
      type: 'Head Office',
      color: '#2C5AA0',
    },
    {
      id: 2,
      city: 'Kochi',
      country: 'India',
      address: '4th Floor, Teepeeyem Square, MG Road, Opposite Cochin Shipyard, Ravipuram, Kochi - 682015',
      phone: '+91 7356475308, +91 7736715551',
      type: 'Branch Office',
      color: '#FF6B35',
    },
    {
      id: 3,
      city: 'Kottayam',
      country: 'India',
      address: 'Good Shepherd College, Ettupady Road, Opposite Karimpin Taste Land, MC Road, Nattakom, Kottayam- 686012',
      phone: '+91 7356475308, +91 7736715551',
      type: 'Branch Office',
      color: '#10B981',
    },
    {
      id: 4,
      city: 'Chennai',
      country: 'India',
      address: 'Old Door No. 3, New, 20, Pycrofts Garden Rd, Chennai, Tamil Nadu 600006',
      phone: '+91 7356475308, +91 7736715551',
      type: 'Branch Office',
      color: '#8B5CF6',
    },
    {
      id: 5,
      city: 'Brampton',
      country: 'Canada',
      address: '10 Billiter Road, L7A4G8, Brampton',
      phone: '+1 (905) 123-4567',
      type: 'International Office',
      color: '#F59E0B',
    },
    {
      id: 6,
      city: 'Stanmore',
      country: 'UK',
      address: 'Devonshire House, 582 Honeypot Lane, Stanmore',
      phone: '+44 20 1234 5678',
      type: 'International Office',
      color: '#EF4444',
    },
    {
      id: 7,
      city: 'Columbus',
      country: 'USA',
      address: '8425 Pulsar Pl, Suite 280 Columbus, OH 43240',
      phone: '+1 (614) 123-4567',
      type: 'International Office',
      color: '#06B6D4',
    },
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
      },
    },
  };

  const cardVariants = {
    hidden: { opacity: 0, y: 50 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        ease: 'easeOut',
      },
    },
  };

  return (
    <Box
      id="offices"
      sx={{
        py: { xs: 8, md: 12 },
        background: 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)',
        position: 'relative',
      }}
    >
      <Container maxWidth="lg">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
        >
          <Typography
            variant="h2"
            sx={{
              textAlign: 'center',
              mb: 2,
              color: 'text.primary',
              fontWeight: 700,
            }}
          >
            Our Office Locations
          </Typography>
          <Typography
            variant="h6"
            sx={{
              textAlign: 'center',
              mb: 6,
              color: 'text.secondary',
              maxWidth: '600px',
              mx: 'auto',
            }}
          >
            Visit us at any of our 7 office locations worldwide
          </Typography>
        </motion.div>

        <motion.div
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
        >
          <Grid container spacing={3}>
            {offices.map((office, index) => (
              <Grid item xs={12} sm={6} md={4} key={office.id}>
                <motion.div variants={cardVariants}>
                  <Card
                    sx={{
                      height: '100%',
                      borderRadius: 3,
                      boxShadow: theme.shadows[2],
                      transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                      position: 'relative',
                      overflow: 'hidden',
                      '&:hover': {
                        transform: 'translateY(-8px)',
                        boxShadow: theme.shadows[6],
                        '&::before': {
                          transform: 'scaleX(1)',
                        },
                      },
                      '&::before': {
                        content: '""',
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        height: '4px',
                        background: office.color,
                        transform: 'scaleX(0)',
                        transition: 'transform 0.3s ease',
                      },
                    }}
                  >
                    <CardContent sx={{ p: 3 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                        <Box
                          sx={{
                            width: 12,
                            height: 12,
                            borderRadius: '50%',
                            backgroundColor: office.color,
                            mr: 1,
                          }}
                        />
                        <Typography
                          variant="caption"
                          sx={{
                            color: office.color,
                            fontWeight: 600,
                            textTransform: 'uppercase',
                            letterSpacing: '0.05em',
                          }}
                        >
                          {office.type}
                        </Typography>
                      </Box>

                      <Typography
                        variant="h5"
                        sx={{
                          color: 'text.primary',
                          fontWeight: 600,
                          mb: 1,
                        }}
                      >
                        {office.city}
                      </Typography>

                      <Typography
                        variant="body2"
                        sx={{
                          color: 'text.secondary',
                          mb: 2,
                          fontWeight: 500,
                        }}
                      >
                        {office.country}
                      </Typography>

                      <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 2 }}>
                        <LocationOn
                          sx={{
                            fontSize: '1.2rem',
                            color: 'text.secondary',
                            mr: 1,
                            mt: 0.2,
                            flexShrink: 0,
                          }}
                        />
                        <Typography
                          variant="body2"
                          sx={{
                            color: 'text.secondary',
                            lineHeight: 1.6,
                            fontSize: '0.9rem',
                          }}
                        >
                          {office.address}
                        </Typography>
                      </Box>

                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Phone
                          sx={{
                            fontSize: '1.2rem',
                            color: 'text.secondary',
                            mr: 1,
                            flexShrink: 0,
                          }}
                        />
                        <Typography
                          variant="body2"
                          sx={{
                            color: 'text.secondary',
                            fontWeight: 500,
                            fontSize: '0.9rem',
                          }}
                        >
                          {office.phone}
                        </Typography>
                      </Box>
                    </CardContent>
                  </Card>
                </motion.div>
              </Grid>
            ))}
          </Grid>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.4 }}
          viewport={{ once: true }}
        >
          <Box sx={{ textAlign: 'center', mt: 6 }}>
            <Typography
              variant="h6"
              sx={{
                color: 'text.secondary',
                mb: 3,
                fontWeight: 500,
              }}
            >
              Can't find an office near you?
            </Typography>
            <motion.div
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              <Button
                variant="outlined"
                size="large"
                startIcon={<Public />}
                onClick={() => {
                  const element = document.getElementById('contact');
                  if (element) {
                    element.scrollIntoView({ behavior: 'smooth' });
                  }
                }}
                sx={{
                  borderColor: 'primary.main',
                  color: 'primary.main',
                  borderRadius: 3,
                  px: 4,
                  py: 1.5,
                  fontSize: '1.1rem',
                  fontWeight: 600,
                  textTransform: 'none',
                  '&:hover': {
                    backgroundColor: 'primary.main',
                    color: 'white',
                    transform: 'translateY(-2px)',
                  },
                  transition: 'all 0.3s ease',
                }}
              >
                Contact Us Online
              </Button>
            </motion.div>
          </Box>
        </motion.div>
      </Container>
    </Box>
  );
};

export default Offices;
